import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useConsentDraft } from '../../contexts/ConsentDraftContext';
import { useTheme } from '../../contexts/ThemeContext';
import { apiService } from '../../services/api';
import type { ConsentDefinitionDto, PriorConsentAnswerDto } from '../../types/consent';
import { ApiError } from '../../types/api/ApiError';
import { getInitials } from '../../utils/helpers';
import { sanitizeConsentHtml } from '../../utils/sanitizeHtml';
import {
  areAllConsentsAnswered,
  getEffectiveConsentValue,
  getServerTriState,
} from '../../utils/consentCompletion';
import InfoSection from '../InfoSection/InfoSection';
import ConsentTriYesNo, { ConsentTriValue } from '../ConsentTriYesNo/ConsentTriYesNo';
import '../SchoolMealForm/SchoolMealForm.css';
import './ParentalConsentsPage.css';

/** Most recent prior answer (current definition version is newer than this row). */
function lastHistoricalPrior(d: ConsentDefinitionDto): PriorConsentAnswerDto | null {
  const rows = d.historicalRows;
  if (!rows?.length) return null;
  const sorted = [...rows].sort((a, b) => {
    const ta = Date.parse(a.modified || '');
    const tb = Date.parse(b.modified || '');
    if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) {
      return tb - ta;
    }
    const va = parseInt(a.respondedVersion || '', 10);
    const vb = parseInt(b.respondedVersion || '', 10);
    if (!Number.isNaN(va) && !Number.isNaN(vb) && va !== vb) {
      return vb - va;
    }
    return 0;
  });
  return sorted[0] ?? null;
}

function shouldShowPriorHistory(c: ConsentDefinitionDto, prior: PriorConsentAnswerDto | null): boolean {
  if (!prior) return false;
  if (c.isUpdatedSincePriorAnswer) return true;
  const answeredOn = (prior.respondedVersion || '').trim();
  const cv = (c.consentVersion || '').trim();
  if (!answeredOn || !cv || answeredOn === cv) return false;
  const r = (c.currentConsentResponse || '').trim();
  const defResponded = (c.respondedVersion || '').trim();
  const settledOnCurrent =
    (r === 'Yes' || r === 'No') && (!defResponded || defResponded === cv);
  return !settledOnCurrent;
}

function formatPriorDate(iso?: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

type PriorAnswerKind = 'yes' | 'no' | 'other';

function priorAnswerKind(response: string | null | undefined): PriorAnswerKind {
  const r = (response || '').trim().toLowerCase();
  if (r === 'yes') return 'yes';
  if (r === 'no') return 'no';
  return 'other';
}

function PriorAnswerPill({ response }: { response: string | null | undefined }) {
  const { t } = useTranslation();
  const kind = priorAnswerKind(response);
  const label =
    kind === 'yes'
      ? t('form.answersSection.yes')
      : kind === 'no'
        ? t('form.answersSection.no')
        : (response || '').trim() || '—';

  return (
    <span className={`consent-prior-answer-pill consent-prior-answer-pill--${kind}`}>{label}</span>
  );
}

const ParentalConsentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const {
    formData,
    consentData,
    consentLoadError,
    isConsentDetailLoading,
    refreshConsentData,
    replaceConsentData,
  } = useAuth();
  const { draft, setDraft, setChoice, resetDraft } = useConsentDraft();
  const [searchParams] = useSearchParams();
  const studentId = searchParams.get('studentId')?.trim() ?? '';

  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedPrior, setExpandedPrior] = useState<Record<number, boolean>>({});
  const [consentLoadBannerDismissed, setConsentLoadBannerDismissed] = useState(false);

  useEffect(() => {
    setConsentLoadBannerDismissed(false);
  }, [consentLoadError]);

  useEffect(() => {
    return () => resetDraft();
  }, [resetDraft]);

  const definitions = consentData?.definitions ?? [];
  const showConsentLoadErrorBanner = Boolean(consentLoadError) && !consentLoadBannerDismissed;

  const logo = theme === 'light'
    ? '/IST logo vector_Logo light.svg'
    : '/IST logo vector_Logo.svg';

  const studentInfo = useMemo(
    () => consentData?.studentInfo ?? formData?.studentInfo,
    [consentData?.studentInfo, formData?.studentInfo]
  );

  const parentInitials = getInitials(studentInfo?.parentName || '');

  const effectiveValue = (c: ConsentDefinitionDto): ConsentTriValue =>
    getEffectiveConsentValue(c, draft);

  const allItemsHaveChoice = useMemo(
    () => areAllConsentsAnswered(consentData?.definitions ?? [], draft),
    [consentData, draft]
  );

  const hasChangesToSubmit = useMemo(() => {
    const defs = consentData?.definitions ?? [];
    return defs.some((c) => {
      if (!Object.prototype.hasOwnProperty.call(draft, c.definitionItemId)) return false;
      const ch = draft[c.definitionItemId];
      if (ch !== 'Yes' && ch !== 'No') return false;
      return ch !== getServerTriState(c);
    });
  }, [consentData, draft]);

  const canSubmit = allItemsHaveChoice && hasChangesToSubmit;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);
    setConflictMessage(null);

    if (
      !definitions.every((c) => {
        const v = getEffectiveConsentValue(c, draft);
        return v === 'Yes' || v === 'No';
      })
    ) {
      return;
    }

    const toSave = definitions.filter((c) => {
      if (!Object.prototype.hasOwnProperty.call(draft, c.definitionItemId)) return false;
      const ch = draft[c.definitionItemId];
      if (ch !== 'Yes' && ch !== 'No') return false;
      return ch !== getServerTriState(c);
    });

    if (toSave.length === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      for (const c of toSave) {
        const response = draft[c.definitionItemId] as 'Yes' | 'No';
        await apiService.saveConsentResponse({
          studentId,
          definitionItemId: c.definitionItemId,
          response,
          ...(c.currentResponseItemId != null
            ? { responseItemId: c.currentResponseItemId }
            : {}),
        });
      }

      const savedIds = new Set(toSave.map((c) => c.definitionItemId));
      if (consentData) {
        replaceConsentData({
          ...consentData,
          definitions: consentData.definitions.map((d) => {
            if (!savedIds.has(d.definitionItemId)) return d;
            const response = draft[d.definitionItemId] as 'Yes' | 'No';
            const cv = (d.consentVersion || '').trim();
            return {
              ...d,
              currentConsentResponse: response,
              ...(cv ? { respondedVersion: cv } : {}),
              isUpdatedSincePriorAnswer: false,
            };
          }),
        });
      }
      setDraft((prev) => {
        const next = { ...prev };
        for (const c of toSave) delete next[c.definitionItemId];
        return next;
      });

      setSubmitSuccess(t('consents.submitSuccess'));
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409) {
        setConflictMessage(apiErr.message || t('consents.conflictMessage'));
        await refreshConsentData();
        setDraft({});
      } else {
        setSubmitError(apiErr.message || t('consents.saveError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePrior = (id: number) => {
    setExpandedPrior((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!studentId) {
    return (
      <div className="school-meal-form-container parental-consents-page">
        <p className="consents-error-banner" role="alert">
          {t('consents.missingStudentId')}
        </p>
      </div>
    );
  }

  const showConsentLoading =
    isConsentDetailLoading || (!consentData && !consentLoadError);

  if (showConsentLoading && !consentData) {
    return (
      <div className="school-meal-form-container parental-consents-page consents-loading">
        <p>{t('consents.loading')}</p>
      </div>
    );
  }

  if (showConsentLoadErrorBanner && !consentData) {
    return (
      <div className="school-meal-form-container parental-consents-page">
        <div className="consents-error-banner" role="alert">
          <span className="consents-banner-text">{consentLoadError}</span>
          <button
            type="button"
            className="form-message-close"
            onClick={() => setConsentLoadBannerDismissed(true)}
            aria-label={t('consents.dismissError')}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-meal-form-container parental-consents-page">
      <form
        className="school-meal-form parental-consents-form"
        onSubmit={handleSubmit}
        aria-label={t('consents.formAriaLabel')}
      >
        <div className="form-header">
          <img src={logo} alt="IST Logo" className="form-logo" />
          <div className="form-title-row">
            <h1 className="form-title">{t('consents.pageTitle')}</h1>
            {studentInfo?.parentName && (
              <div className="parent-user-indicator">
                <div className="parent-user-avatar">
                  <span className="parent-avatar-initials">{parentInitials}</span>
                </div>
                <span className="parent-user-name">{studentInfo.parentName}</span>
              </div>
            )}
          </div>
        </div>

        {studentInfo && <InfoSection studentInfo={studentInfo} />}

        {showConsentLoadErrorBanner && (
          <div className="consents-error-banner consents-inline-banner" role="alert">
            <span className="consents-banner-text">{consentLoadError}</span>
            <button
              type="button"
              className="form-message-close"
              onClick={() => setConsentLoadBannerDismissed(true)}
              aria-label={t('consents.dismissError')}
            >
              ×
            </button>
          </div>
        )}

        {conflictMessage && (
          <div className="consents-conflict-banner consents-inline-banner" role="alert">
            <span className="consents-banner-text">{conflictMessage}</span>
            <button
              type="button"
              className="form-message-close"
              onClick={() => setConflictMessage(null)}
              aria-label={t('consents.dismissConflict')}
            >
              ×
            </button>
          </div>
        )}

        <div className="consent-items">
          {definitions.map((c) => {
            const prior = lastHistoricalPrior(c);
            const showPrior = shouldShowPriorHistory(c, prior);
            const expanded = expandedPrior[c.definitionItemId];

            return (
              <section key={c.definitionItemId} className="consent-card">
                <div className="consent-card-header">
                  <h2 className="consent-card-title">{c.title}</h2>
                  {showPrior && (
                    <span className="consent-updated-badge">{t('consents.updatedBadge')}</span>
                  )}
                </div>

                {showPrior && prior?.consentResponse && (
                  <p className="consent-prior-summary">
                    <span className="consent-prior-summary-text">
                      {t('consents.priorAnswerLinePrefix', { date: formatPriorDate(prior.modified) })}
                    </span>
                    <PriorAnswerPill response={prior.consentResponse} />
                  </p>
                )}

                <div
                  className="consent-detailed-text"
                  dangerouslySetInnerHTML={{ __html: sanitizeConsentHtml(c.detailedTextHtml ?? '') }}
                />

                {showPrior && prior && (
                  <div className="consent-prior-expand">
                    <button
                      type="button"
                      className="consent-prior-toggle"
                      onClick={() => togglePrior(c.definitionItemId)}
                    >
                      {expanded ? t('consents.hidePreviousVersion') : t('consents.showPreviousVersion')}
                    </button>
                    {expanded && (
                      <div className="consent-prior-panel">
                        {prior.consentResponse ? (
                          <div className="consent-prior-panel-heading">
                            <PriorAnswerPill response={prior.consentResponse} />
                          </div>
                        ) : null}
                        <div
                          className="consent-prior-html"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeConsentHtml(prior.consentTextSnapshot || ''),
                          }}
                        />
                        {c.changeDescription && (
                          <div className="consent-what-changed">
                            <h4 className="consent-what-changed-title">{t('consents.whatChanged')}</h4>
                            <p>{c.changeDescription}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <ConsentTriYesNo value={effectiveValue(c)} onChange={(v) => setChoice(c.definitionItemId, v)} />
              </section>
            );
          })}
        </div>

        {submitError && (
          <div className="form-error" role="alert">
            <span className="form-error-text">{submitError}</span>
            <button type="button" className="form-message-close" onClick={() => setSubmitError(null)} aria-label="Close">
              ×
            </button>
          </div>
        )}

        {submitSuccess && (
          <div className="form-success" role="alert">
            <span className="form-success-text">{submitSuccess}</span>
            <button type="button" className="form-message-close" onClick={() => setSubmitSuccess(null)} aria-label="Close">
              ×
            </button>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="button button-primary"
            disabled={isSubmitting || !canSubmit}
            title={
              !allItemsHaveChoice
                ? t('consents.completeAllBeforeSave')
                : !hasChangesToSubmit
                  ? t('consents.nothingToSave')
                  : undefined
            }
          >
            {isSubmitting ? t('form.submitting') : t('consents.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ParentalConsentsPage;
